import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users as UsersIcon,
  Search,
  Edit,
  Trash2,
  RefreshCw,
  AlertCircle,
  UserPlus,
  Key,
  Power,
  Copy,
  Check,
  Eye,
  EyeOff,
  Building2,
  User,
  Link2,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../../components/ui';
import { formatDate } from '../../lib/utils';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface Entity {
  id: string;
  name: string;
  type: 'COMPANY' | 'INDIVIDUAL';
  document: string;
}

interface UserEntity {
  id: string;
  name: string;
  type: 'COMPANY' | 'INDIVIDUAL';
}

interface User {
  id: string;
  name: string;
  email: string;
  cpfCnpj: string;
  phone?: string;
  type: 'ADMIN' | 'OPERATOR' | 'COMPLIANCE' | 'EMPLOYEE' | 'CUSTOMER' | 'SUPPLIER';
  status: 'ACTIVE' | 'INACTIVE' | 'BLOCKED';
  createdAt: string;
  entityId?: string | null;
  entity?: UserEntity | null;
}

const userTypeLabels = {
  ADMIN: 'Administrador',
  OPERATOR: 'Operador',
  COMPLIANCE: 'Compliance',
  EMPLOYEE: 'Funcionário',
  CUSTOMER: 'Cliente',
  SUPPLIER: 'Fornecedor',
};

const userTypeColors = {
  ADMIN: 'primary',
  OPERATOR: 'info',
  COMPLIANCE: 'warning',
  EMPLOYEE: 'info',
  CUSTOMER: 'success',
  SUPPLIER: 'warning',
} as const;

const statusLabels = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  BLOCKED: 'Bloqueado',
};


export function AdminUsers() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cpfCnpj: '',
    phone: '',
    type: 'EMPLOYEE' as User['type'],
    password: '',
    entityId: '' as string,
  });
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Password validation
  const isPasswordValid = formData.password.length >= 8;
  const showPasswordError = passwordTouched && !isPasswordValid && formData.password.length > 0;

  // Fetch entities for linking
  const { data: entitiesData } = useQuery({
    queryKey: ['entities-for-user-linking'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.entities.list);
      return response.data as Entity[];
    },
  });

  const entityOptions = [
    { value: '', label: 'Nenhuma (sem vínculo)' },
    ...(entitiesData || [])
      .filter((e: Entity) => e)
      .map((e: Entity) => ({
        value: e.id,
        label: `${e.name} (${e.type === 'COMPANY' ? 'PJ' : 'PF'})`,
      })),
  ];

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.users.list);
      return response.data as { users: User[]; total: number };
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData & { entityId: string | null }) => {
      const response = await api.post(ENDPOINTS.users.create, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> & { entityId?: string | null } }) => {
      const response = await api.put(ENDPOINTS.users.update(id), data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setIsModalOpen(false);
      setEditingUser(null);
      resetForm();
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async ({ id, newPassword }: { id: string; newPassword: string }) => {
      const response = await api.post(ENDPOINTS.users.resetPassword(id), { newPassword });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setGeneratedPassword(newPassword);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: User['status'] }) => {
      const response = await api.put(ENDPOINTS.users.update(id), { status });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(ENDPOINTS.users.delete(id));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      cpfCnpj: '',
      phone: '',
      type: 'EMPLOYEE',
      password: '',
      entityId: '',
    });
    setPasswordTouched(false);
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      cpfCnpj: user.cpfCnpj,
      phone: user.phone || '',
      type: user.type,
      password: '',
      entityId: user.entityId || '',
    });
    setIsModalOpen(true);
  };

  const handleResetPassword = (user: User) => {
    setSelectedUserForPassword(user);
    const pwd = generatePassword();
    setNewPassword(pwd);
    setGeneratedPassword(null);
    setPasswordCopied(false);
    setPasswordTouched(false);
    setIsPasswordModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUser) {
      // Only send fields that can be updated
      const { password, cpfCnpj, email, entityId, ...updateData } = formData;
      const dataToSend: Record<string, unknown> = {
        ...updateData,
        entityId: entityId || null,
      };
      if (password) {
        dataToSend.password = password;
      }
      updateMutation.mutate({ 
        id: editingUser.id, 
        data: dataToSend as Partial<typeof formData> & { entityId?: string | null }
      });
    } else {
      const dataToSend = {
        ...formData,
        entityId: formData.entityId || null,
      } as typeof formData & { entityId: string | null };
      createMutation.mutate(dataToSend);
    }
  };

  const handlePasswordReset = () => {
    if (selectedUserForPassword && newPassword) {
      resetPasswordMutation.mutate({ 
        id: selectedUserForPassword.id, 
        newPassword 
      });
    }
  };

  const handleToggleStatus = (user: User) => {
    const newStatus = user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    if (confirm(`Deseja ${newStatus === 'ACTIVE' ? 'ativar' : 'inativar'} o usuário ${user.name}?`)) {
      toggleStatusMutation.mutate({ id: user.id, status: newStatus });
    }
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir este usuário? Esta ação irá inativá-lo.')) {
      deleteMutation.mutate(id);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  const users = data?.users || [];
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar usuários</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Usuários</h1>
          <p className="text-surface-400">Gerencie os usuários do sistema</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setEditingUser(null); setIsModalOpen(true); }}>
          <UserPlus className="w-4 h-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardBody>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por nome ou email..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="w-5 h-5 text-primary-400" />
            Lista de Usuários
            <Badge variant="info">{filteredUsers.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-16" />
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <UsersIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum usuário encontrado</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-4 hover:bg-surface-800/30 transition-colors gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center shrink-0">
                      <span className="text-primary-400 font-semibold">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-surface-100 truncate">{user.name}</p>
                      <p className="text-sm text-surface-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 pl-13 sm:pl-0">
                    {/* Entity Link */}
                    {user.entity ? (
                      <div className="flex items-center gap-1.5 px-2 py-1 bg-surface-800 rounded text-xs">
                        {user.entity.type === 'COMPANY' ? (
                          <Building2 className="w-3 h-3 text-primary-400" />
                        ) : (
                          <User className="w-3 h-3 text-accent-400" />
                        )}
                        <span className="text-surface-300 max-w-[120px] truncate" title={user.entity.name}>
                          {user.entity.name}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-surface-600">
                        <Link2 className="w-3 h-3" />
                        <span>Sem vínculo</span>
                      </div>
                    )}
                    <Badge variant={userTypeColors[user.type]}>
                      {userTypeLabels[user.type]}
                    </Badge>
                    <Badge variant={user.status === 'ACTIVE' ? 'success' : user.status === 'BLOCKED' ? 'error' : 'warning'}>
                      {statusLabels[user.status]}
                    </Badge>
                    <span className="text-sm text-surface-500 hidden sm:inline">
                      {formatDate(user.createdAt)}
                    </span>
                    <div className="flex items-center gap-1 ml-auto sm:ml-0">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} title="Editar usuário">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleResetPassword(user)} title="Redefinir senha">
                        <Key className="w-4 h-4 text-yellow-400" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(user)} title={user.status === 'ACTIVE' ? 'Inativar usuário' : 'Ativar usuário'}>
                        <Power className={`w-4 h-4 ${user.status === 'ACTIVE' ? 'text-green-400' : 'text-surface-500'}`} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(user.id)} title="Excluir usuário">
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>


      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingUser(null); resetForm(); }}
        title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
          <Input
            label="CPF/CNPJ"
            value={formData.cpfCnpj}
            onChange={(e) => setFormData({ ...formData, cpfCnpj: e.target.value })}
            required
            placeholder="Apenas números (11 para CPF, 14 para CNPJ)"
          />
          <Input
            label="Telefone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <Select
            label="Tipo"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as User['type'] })}
            options={[
              { value: 'ADMIN', label: '👑 Administrador' },
              { value: 'OPERATOR', label: '⚙️ Operador' },
              { value: 'COMPLIANCE', label: '🛡️ Compliance' },
              { value: 'EMPLOYEE', label: '👤 Funcionário' },
              { value: 'CUSTOMER', label: '🧑‍💼 Cliente' },
              { value: 'SUPPLIER', label: '📦 Fornecedor' },
            ]}
          />
          <Select
            label="Entidade Vinculada"
            value={formData.entityId}
            onChange={(e) => setFormData({ ...formData, entityId: e.target.value })}
            options={entityOptions}
          />
          <p className="text-xs text-surface-400 -mt-2">
            Vincular o usuário a uma entidade permite controle de acesso baseado em hierarquia organizacional.
          </p>
          {!editingUser && (
            <div className="space-y-2">
              <Input
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                onBlur={() => setPasswordTouched(true)}
                required
                error={showPasswordError ? 'A senha deve ter no mínimo 8 caracteres' : undefined}
                hint={!showPasswordError && formData.password.length === 0 ? 'Mínimo de 8 caracteres' : undefined}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-surface-400 hover:text-surface-200"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setFormData({ ...formData, password: generatePassword() })}
              >
                Gerar senha aleatória
              </Button>
            </div>
          )}
          {editingUser && (
            <div className="p-3 bg-surface-800/50 rounded-lg border border-surface-700">
              <p className="text-sm text-surface-400">
                💡 Para alterar a senha, use o botão <Key className="w-3 h-3 inline" /> na lista de usuários.
              </p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              isLoading={createMutation.isPending || updateMutation.isPending}
              disabled={!editingUser && !isPasswordValid}
            >
              {editingUser ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>


      {/* Reset Password Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => { 
          setIsPasswordModalOpen(false); 
          setSelectedUserForPassword(null); 
          setGeneratedPassword(null);
          setNewPassword('');
        }}
        title="Redefinir Senha"
      >
        <div className="space-y-4">
          {selectedUserForPassword && (
            <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
              <p className="text-surface-300">
                Redefinindo senha para: <span className="font-semibold text-surface-100">{selectedUserForPassword.name}</span>
              </p>
              <p className="text-sm text-surface-500">{selectedUserForPassword.email}</p>
            </div>
          )}

          {!generatedPassword ? (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-surface-300">Nova Senha</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      onBlur={() => setPasswordTouched(true)}
                      placeholder="Digite a nova senha"
                      error={passwordTouched && newPassword.length > 0 && newPassword.length < 8 ? 'A senha deve ter no mínimo 8 caracteres' : undefined}
                      hint={newPassword.length === 0 ? 'Mínimo de 8 caracteres' : undefined}
                    />
                  </div>
                  <Button type="button" variant="secondary" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => setNewPassword(generatePassword())}>
                    Gerar
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="secondary" onClick={() => setIsPasswordModalOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="button" 
                  variant="primary" 
                  onClick={handlePasswordReset}
                  isLoading={resetPasswordMutation.isPending}
                  disabled={!newPassword || newPassword.length < 8}
                >
                  Redefinir Senha
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 font-medium mb-2">✅ Senha redefinida com sucesso!</p>
                <p className="text-sm text-surface-400 mb-3">
                  Copie a nova senha e envie para o usuário:
                </p>
                <div className="flex items-center gap-2 p-3 bg-surface-900 rounded border border-surface-700">
                  <code className="flex-1 text-primary-400 font-mono">
                    {showPassword ? generatedPassword : '••••••••••••'}
                  </code>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => copyToClipboard(generatedPassword!)}>
                    {passwordCopied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  type="button" 
                  variant="primary" 
                  onClick={() => {
                    setIsPasswordModalOpen(false);
                    setSelectedUserForPassword(null);
                    setGeneratedPassword(null);
                    setNewPassword('');
                  }}
                >
                  Entendi
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </motion.div>
  );
}
